/**
 * ItinerarioMusicalPDF.js
 * Formato profesional de requerimientos de audio · A la Vuelta 360°
 * Página 1 – Cabecera + datos del cliente + itinerario del evento
 * Página 2 – Preferencias musicales + música a evitar + requisitos técnicos
 */
import React from "react";
import {
  Document, Page, Text, View, Image, StyleSheet, Svg, Path,
} from "@react-pdf/renderer";

// ── Sin Font.register() - usar solo fuentes estándar PDF ──────────────────────
// Helvetica-Oblique es nativa en react-pdf, no necesita registro ni CDN
// Esto evita los errores: "Font not registered" y "Buffer is not defined"

const F  = "Helvetica";
const FB = "Helvetica-Bold";
const FO = "Helvetica-Oblique";

const C = {
  black:"#000000", white:"#FFFFFF", brand:"#1A1A2E", 
  accent:"#D67357", accentLt:"#F5E6E1", gray:"#4B5563", grayLt:"#9CA3AF", 
  border:"#D1D5DB", rowAlt:"#F9FAFB", infoBox:"#FFF8F5", infoBorder:"#D67357",
};

const IcoCheck = () => (<Svg width={10} height={10} viewBox="0 0 24 24"><Path fill="#7C3AED" d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z"/></Svg>);
const IcoSpeaker = () => (<Svg width={10} height={10} viewBox="0 0 24 24"><Path fill="#7C3AED" d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02zM14 3.23v2.06c2.89.86 5 3.54 5 6.71s-2.11 5.85-5 6.71v2.06c4.01-.91 7-4.49 7-8.77 0-4.28-2.99-7.86-7-8.77z"/></Svg>);
const IcoBolt = () => (<Svg width={10} height={10} viewBox="0 0 24 24"><Path fill="#7C3AED" d="M7 2v11h3v9l7-12h-4l4-8z"/></Svg>);
const IcoShield = () => (<Svg width={10} height={10} viewBox="0 0 24 24"><Path fill="#7C3AED" d="M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4z"/></Svg>);
const IcoClock = () => (<Svg width={10} height={10} viewBox="0 0 24 24"><Path fill="#7C3AED" d="M11.99 2C6.47 2 2 6.48 2 12s4.47 10 9.99 10C17.52 22 22 17.52 22 12S17.52 2 11.99 2zM12 20c-4.42 0-8-3.58-8-8s3.58-8 8-8 8 3.58 8 8-3.58 8-8 8zm.5-13H11v6l5.25 3.15.75-1.23-4.5-2.67V7z"/></Svg>);
const IcoDJ = () => (<Svg width={10} height={10} viewBox="0 0 24 24"><Path fill="#7C3AED" d="M12 3v9.28c-.47-.17-.97-.28-1.5-.28C8.01 12 6 14.01 6 16.5S8.01 21 10.5 21c2.31 0 4.2-1.75 4.45-4H15V6h4V3h-7z"/></Svg>);

const S = StyleSheet.create({
  page:{ fontFamily:F, backgroundColor:C.white, paddingTop:16, paddingBottom:68, paddingLeft:30, paddingRight:30 },
  // Header estilo recibo - logo a la izquierda + título a la derecha
  header:{ flexDirection:"row", alignItems:"center", justifyContent:"space-between", marginBottom:10, gap:20 },
  logoCircle:{ width:56, height:56, borderRadius:28, overflow:"hidden", flexShrink:0 },
  logoImg:{ width:56, height:56 },
  logoFallback:{ width:56, height:56, borderRadius:28, backgroundColor:C.brand, justifyContent:"center", alignItems:"center" },
  logoFallbackText:{ fontFamily:FB, fontSize:9, color:C.white, textAlign:"center" },
  headerContent:{ flex:1, alignItems:"flex-end" },
  brandTitle:{ fontFamily:FO, fontSize:32, color:C.black },
  brandSubtitle:{ fontFamily:F, fontSize:8, color:C.gray, letterSpacing:1, marginTop:2 },
  // Barra de título con color naranja/salmón (estilo recibo)
  titleBar:{ backgroundColor:C.accent, paddingHorizontal:12, paddingVertical:7, marginBottom:10, flexDirection:"row", justifyContent:"flex-start" },
  titleBarText:{ fontFamily:FB, fontSize:9.5, color:C.white, letterSpacing:1 },
  // Información en 3 columnas (estilo recibo)
  infoContainer:{ flexDirection:"row", gap:12, marginBottom:12 },
  infoColumn:{ flex:1, borderLeftWidth:4, paddingLeft:12, backgroundColor:C.rowAlt },
  infoColumnFirst:{ borderLeftColor:C.accent },
  infoColumnSecond:{ borderLeftColor:C.brand },
  infoColumnThird:{ borderLeftColor:C.grayLt },
  infoColumnTitle:{ fontFamily:FB, fontSize:7.5, color:C.accent, letterSpacing:0.8, marginBottom:6 },
  infoName:{ fontFamily:FB, fontSize:9, color:C.black, marginBottom:3 },
  infoSmall:{ fontFamily:F, fontSize:7.5, color:C.gray, lineHeight:1.4, marginBottom:2 },
  infoValueBold:{ fontFamily:FB, fontSize:8, color:C.black, marginBottom:2 },
  confirmBadge:{ backgroundColor:"#E3F2FD", paddingHorizontal:8, paddingVertical:4, borderRadius:3, alignSelf:"flex-start", marginTop:4 },
  confirmText:{ fontFamily:FB, fontSize:7, color:"#1976D2", letterSpacing:0.5 },
  dividerAccent:{ height:1, backgroundColor:C.accent, marginBottom:12 },
  docTitle:{ fontFamily:FB, fontSize:11, color:C.white, textAlign:"center", letterSpacing:1.2, backgroundColor:C.brand, paddingVertical:6, marginBottom:10 },
  introText:{ fontFamily:F, fontSize:9, color:C.gray, textAlign:"justify", lineHeight:1.6, marginBottom:10 },
  introBold:{ fontFamily:FB, fontSize:9, color:C.black },
  sectionHeader:{ flexDirection:"row", alignItems:"center", backgroundColor:C.brand, borderRadius:3, paddingHorizontal:10, paddingVertical:5, marginBottom:6, gap:6 },
  sectionTitle:{ fontFamily:FB, fontSize:8, color:C.white, letterSpacing:0.6 },
  table:{ borderWidth:0.5, borderColor:C.border, marginBottom:10 },
  tableHead:{ flexDirection:"row", backgroundColor:C.brand },
  tableRow:{ flexDirection:"row", borderBottomWidth:0.5, borderBottomColor:C.border, minHeight:18 },
  tableRowAlt:{ backgroundColor:C.rowAlt },
  thCell:{ fontFamily:FB, fontSize:7.5, color:C.white, paddingVertical:5, paddingHorizontal:4, letterSpacing:0.4 },
  tdCell:{ fontFamily:F, fontSize:7.5, color:C.black, paddingVertical:3, paddingHorizontal:4 },
  colHora:      { width:"13%", borderRightWidth:0.5, borderRightColor:C.border },
  colActiv:      { width:"35%", borderRightWidth:0.5, borderRightColor:C.border },
  colMusica:     { width:"29%", borderRightWidth:0.5, borderRightColor:C.border },
  colCoord:      { width:"23%" },
  colHoraHdr:    { width:"13%", borderRightWidth:0.5, borderRightColor:"rgba(255,255,255,0.2)" },
  colActivHdr:   { width:"35%", borderRightWidth:0.5, borderRightColor:"rgba(255,255,255,0.2)" },
  colMusicaHdr:  { width:"29%", borderRightWidth:0.5, borderRightColor:"rgba(255,255,255,0.2)" },
  colCoordHdr:   { width:"23%" },
  fillRow:{ flexDirection:"row", alignItems:"flex-end", marginBottom:7, gap:4 },
  fillLabel:{ fontFamily:FB, fontSize:8, color:C.gray, flexShrink:0 },
  fillLine:{ flex:1, borderBottomWidth:0.5, borderBottomColor:C.black, marginBottom:1, height:11 },
  reqBlock:{ marginBottom:7 },
  reqTitleRow:{ flexDirection:"row", alignItems:"center", gap:4, marginBottom:2 },
  reqTitle:{ fontFamily:FB, fontSize:8, color:C.black },
  reqItem:{ flexDirection:"row", alignItems:"flex-start", paddingLeft:12, marginBottom:1.5 },
  bullet:{ fontFamily:F, fontSize:8, color:C.accent, marginRight:4, marginTop:0.3 },
  reqText:{ fontFamily:F, fontSize:8, color:C.gray, flex:1, lineHeight:1.4 },
  reqHighlight:{ fontFamily:FB, fontSize:8, color:C.black },
  sectionGap:{ marginBottom:10 },
  footer:{ position:"absolute", bottom:20, left:20, right:30, alignItems:"center", borderTopWidth:0.5, borderTopColor:C.border, paddingTop:5 },
  footerQuote:{ fontFamily:FO, fontSize:8, color:C.gray, textAlign:"center", marginBottom:2 },
  footerBrand:{ fontFamily:FB, fontSize:7.5, color:C.grayLt, textAlign:"center" },
  footerPageNum:{ fontFamily:F, fontSize:8, color:C.gray, textAlign:"center", marginTop:4 },
  headerCompact:{ flexDirection:"row", alignItems:"flex-start", justifyContent:"flex-start", marginBottom:8, gap:10 },
  logoSmallCircle:{ width:28, height:28, borderRadius:14, overflow:"hidden", flexShrink:0 },
  brandCompact:{ fontFamily:FO, fontSize:16, color:C.black, flex:1, marginTop:1 },
});

const fmtFecha = (str) => {
  if (!str) return "";
  try {
    const d = new Date(str.includes("T") ? str : str + "T12:00:00");
    return d.toLocaleDateString("es-MX", { weekday:"long", day:"numeric", month:"long", year:"numeric" });
  } catch { return str; }
};
const fmtHora = (str) => {
  if (!str) return "";
  const [h, m] = str.split(":").map(Number);
  const ampm = h >= 12 ? "p.m." : "a.m.";
  return `${h % 12 || 12}:${String(m).padStart(2,"0")} ${ampm}`;
};

const LogoImg = ({ url, size=56, radius=28 }) =>
  url ? (
    <Image src={url} style={{ width:size, height:size, borderRadius:radius }} />
  ) : (
    <View style={{ width:size, height:size, borderRadius:radius, backgroundColor:C.brand, justifyContent:"center", alignItems:"center" }}>
      <Text style={{ fontFamily:FB, fontSize:size*0.16, color:C.white, textAlign:"center" }}>360{"\n"}°</Text>
    </View>
  );

const SectionHeader = ({ icon, title }) => (
  <View style={S.sectionHeader}>{icon}<Text style={S.sectionTitle}>{title}</Text></View>
);

const FillLine = ({ label }) => (
  <View style={S.fillRow}>
    <Text style={S.fillLabel}>• {label}:</Text>
    <View style={S.fillLine} />
  </View>
);

const ReqBlock = ({ icon, title, items }) => (
  <View style={S.reqBlock}>
    <View style={S.reqTitleRow}>{icon}<Text style={S.reqTitle}>{title}</Text></View>
    {items.map((item, i) => (
      <View key={i} style={S.reqItem}>
        <Text style={S.bullet}>›</Text>
        <Text style={S.reqText}>{item}</Text>
      </View>
    ))}
  </View>
);

const PageFooter = ({ pageNum, totalPages }) => (
  <View style={S.footer}>
    <Text style={S.footerQuote}>"Creamos momentos inmemorables para tus eventos más especiales"</Text>
    <Text style={S.footerBrand}>Gracias por tu Preferencia · A la Vuelta 360°</Text>
    <Text style={S.footerPageNum}>Página {pageNum} de {totalPages}</Text>
  </View>
);

const ItinerarioMusicalPDF = ({ evento, logoUrl }) => {
  if (!evento) return null;
  const nombre    = evento.cliente_nombre   || "Cliente";
  // Buscar teléfono en múltiples propiedades posibles
  const telefono  = evento.cliente_telefono || evento.telefono || evento.cliente?.telefono || "—";
  const titulo    = evento.titulo            || "Evento";
  const fecha     = fmtFecha(evento.fecha_evento || evento.fecha_inicio);
  const horaIni   = fmtHora(evento.hora_inicio);
  const horaFin   = fmtHora(evento.hora_fin);
  const paquete   = evento.paquete_nombre || evento.tipo_servicio_nombre || "—";
  // Mejorado: Mostrar siempre ambas horas con guion si existen
  const horario   = horaIni && horaFin 
    ? `${horaIni} – ${horaFin}` 
    : horaIni 
    ? horaIni 
    : "—";
  const ROWS = 14;

  return (
    <Document title={`Requerimientos-Audio-${nombre.replace(/\s+/g,"_")}`} author="A la Vuelta 360°" subject="Formato de requerimientos de audio" creator="Agenda A la Vuelta 360°">

      {/* ══ PÁGINA 1 ═══════════════════════════════════════════════════════ */}
      <Page size="A4" style={S.page}>

        {/* Header estilo recibo - logo + título */}
        <View style={S.header}>
          <View style={S.logoCircle}><LogoImg url={logoUrl} size={56} radius={28} /></View>
          <View style={S.headerContent}>
            <Text style={S.brandTitle}>A LA VUELTA 360°</Text>
            <Text style={S.brandSubtitle}>TODO PARA TUS EVENTOS</Text>
          </View>
        </View>

        {/* Barra de título naranja/salmón estilo recibo */}
        <View style={S.titleBar}>
          <Text style={S.titleBarText}>REQUERIMIENTOS DE AUDIO MUSICAL</Text>
        </View>

        {/* Información en 3 columnas estilo recibo */}
        <View style={S.infoContainer}>
          {/* Columna 1: Cliente - borde naranja */}
          <View style={[S.infoColumn, S.infoColumnFirst]}>
            <Text style={S.infoColumnTitle}>INFORMACIÓN DEL CLIENTE</Text>
            <Text style={S.infoName}>{nombre}</Text>
            <Text style={S.infoSmall}>Tel: {telefono}</Text>
            <Text style={S.infoSmall}>{evento.cliente_direccion || evento.evento_direccion || "—"}</Text>
          </View>

          {/* Columna 2: Evento - borde negro */}
          <View style={[S.infoColumn, S.infoColumnSecond]}>
            <Text style={S.infoColumnTitle}>DETALLES DEL EVENTO</Text>
            <Text style={S.infoValueBold}>{titulo}</Text>
            <Text style={S.infoSmall}>{fecha || "—"}</Text>
            <Text style={S.infoSmall}>Hora: {horario}</Text>
            <Text style={S.infoSmall}>{paquete}</Text>
            {evento.estado === "confirmado" && (
              <View style={S.confirmBadge}>
                <Text style={S.confirmText}>CONFIRMADO</Text>
              </View>
            )}
          </View>

          {/* Columna 3: Emisión - borde gris */}
          <View style={[S.infoColumn, S.infoColumnThird]}>
            <Text style={S.infoColumnTitle}>EMISIÓN</Text>
            <Text style={S.infoSmall}>Generado:</Text>
            <Text style={S.infoValueBold}>{new Date().toLocaleDateString("es-MX")}</Text>
            <Text style={S.infoSmall}>{new Date().toLocaleTimeString("es-MX", {hour: "2-digit", minute: "2-digit"})}</Text>
          </View>
        </View>

        <View style={S.dividerAccent} />

        <Text style={S.introText}>
          {"    "}Con el propósito de brindar un{" "}
          <Text style={S.introBold}>servicio de audio de la más alta calidad</Text>
          {", "}solicitamos atentamente su apoyo para revisar y completar la siguiente
          información, a fin de realizar una adecuada{" "}
          <Text style={S.introBold}>planeación y logística de su evento.</Text>
        </Text>

        <SectionHeader icon={<IcoDJ />} title="1.  ITINERARIO DEL EVENTO" />

        <View style={S.table}>
          <View style={S.tableHead}>
            <View style={S.colHoraHdr}><Text style={[S.thCell, { textAlign:"center" }]}>HORA</Text></View>
            <View style={S.colActivHdr}><Text style={[S.thCell, { textAlign:"center" }]}>ACTIVIDAD</Text></View>
            <View style={S.colMusicaHdr}><Text style={[S.thCell, { textAlign:"center" }]}>MÚSICA ESPECÍFICA{"\n"}(OPCIONAL)</Text></View>
            <View style={S.colCoordHdr}><Text style={[S.thCell, { textAlign:"center" }]}>COORDINACIONES{"\n"}EXTRA</Text></View>
          </View>
          {Array.from({ length:ROWS }).map((_,i) => (
            <View key={i} style={[S.tableRow, i%2===1 && S.tableRowAlt]}>
              <View style={S.colHora}><Text style={S.tdCell}> </Text></View>
              <View style={S.colActiv}><Text style={S.tdCell}> </Text></View>
              <View style={S.colMusica}><Text style={S.tdCell}> </Text></View>
              <View style={S.colCoord}><Text style={S.tdCell}> </Text></View>
            </View>
          ))}
        </View>

        <PageFooter pageNum={1} totalPages={2} />
      </Page>

      {/* ══ PÁGINA 2 ═══════════════════════════════════════════════════════ */}
      <Page size="A4" style={S.page}>

        <View style={S.headerCompact}>
          <View style={S.logoSmallCircle}><LogoImg url={logoUrl} size={28} radius={14} /></View>
          <Text style={S.brandCompact}>A LA VUELTA 360°</Text>
        </View>

        <View style={{ marginBottom:8 }} />

        <SectionHeader icon={<IcoSpeaker />} title="2.  PREFERENCIAS MUSICALES" />
        <Text style={[S.introText, { marginBottom:8 }]}>Por favor, indique la música, géneros o artistas que desea escuchar durante su evento:</Text>
        <FillLine label="Géneros musicales de preferencia" />
        <FillLine label="Artistas o grupos favoritos" />
        <FillLine label="Canciones indispensables" />
        <FillLine label="Música para momentos especiales (entrada, cena, vals, brindis, etc.)" />

        <View style={S.sectionGap} />

        <SectionHeader icon={<IcoSpeaker />} title="3.  MÚSICA QUE NO DEBERÁ REPRODUCIRSE" />
        <Text style={[S.introText, { marginBottom:8 }]}>Por favor, indique la música, géneros o artistas que no desea que se reproduzcan durante su evento:</Text>
        <FillLine label="Géneros musicales que no desea" />
        <FillLine label="Artistas o grupos que no desea" />
        <FillLine label="Canciones específicas que no deben tocarse" />
        <FillLine label="Observaciones adicionales" />

        <View style={S.sectionGap} />

        <SectionHeader icon={<IcoCheck />} title="4.  REQUISITOS PARA LA INSTALACIÓN" />

        <ReqBlock icon={<IcoDJ />} title="Espacio para la cabina DJ" items={[
          "Se recomienda un área mínima de 2.0 m de frente por 1.5 m de fondo.",
          "El piso deberá ser firme y nivelado.",
          "El espacio deberá estar libre de obstáculos para permitir la operación segura del equipo.",
        ]} />

        <ReqBlock icon={<IcoSpeaker />} title='Ubicación de las bocinas' items={[
          'Se instalarán 2 bocinas Yamaha de 15", una a cada lado del área principal.',
          "Es indispensable contar con espacio suficiente para sus tripiés, evitando obstrucciones y permitiendo una adecuada distribución del sonido.",
        ]} />

        <ReqBlock icon={<IcoBolt />} title="Energía eléctrica" items={[
          "Se requieren al menos 2 contactos eléctricos de 110 V, exclusivos o de fácil acceso, a máximo 10 metros del área de instalación.",
          "Se recomienda no compartir la misma línea eléctrica con equipos de alto consumo, a fin de garantizar un funcionamiento estable.",
        ]} />

        <ReqBlock icon={<IcoShield />} title="Condiciones del lugar" items={[
          "En eventos al aire libre, el área deberá contar con techo o protección contra sol y lluvia.",
          "El acceso deberá permanecer libre para facilitar el ingreso y montaje del equipo.",
        ]} />

        <ReqBlock icon={<IcoClock />} title="Tiempo de montaje" items={[
          "Requerimos ingresar con una anticipación de 60 a 90 minutos al inicio del evento para realizar la instalación, las pruebas de audio y dejar todo listo.",
        ]} />

        <View style={{ marginTop:4, backgroundColor:C.accentLt, borderRadius:4, padding:8, borderLeftWidth:3, borderLeftColor:C.accent }}>
          <Text style={{ fontFamily:F, fontSize:8, color:C.gray, lineHeight:1.5 }}>
            Agradecemos de antemano su apoyo para cumplir con estos requisitos, lo cual nos permitirá garantizar un{" "}
            <Text style={{ fontFamily:FB, color:C.black }}>montaje seguro y un sonido de excelente calidad</Text>
            {" "}durante su evento.
          </Text>
        </View>

        <PageFooter pageNum={2} totalPages={2} />
      </Page>

    </Document>
  );
};

export default ItinerarioMusicalPDF;
